if (!customElements.get('ss-media-gallery')) {
  customElements.define(
    'ss-media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));

        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event.data && event.data.variant;
          if (!variant) return;
          this.setVariantMedia(variant.id);
        });
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        // Snapshot original product media HTML before any variant swap
        const sliderEl = this.querySelector('[id^="Slider-Gallery"]');
        const thumbsEl = this.querySelector('[id^="Slider-Thumbnails"]');
        if (sliderEl && !this._productSlidesHTML) this._productSlidesHTML = sliderEl.innerHTML;
        if (thumbsEl && !this._productThumbsHTML) this._productThumbsHTML = thumbsEl.innerHTML;

        // Swap HTML before slider initializes so Swiper sees the correct slides
        const selectedVariantInput = document.querySelector('input[name="id"]');
        if (selectedVariantInput) {
          this.swapVariantMediaHTML(selectedVariantInput.value);
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initialized = true;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          grabCursor: true,
          allowTouchMove: true,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;

            let source = {
              id: mediaIndex,
              mediaType: mediaType,
            };

            switch (mediaType) {
              case 'model':
              case 'video':
              case 'external_video':
                const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';

                source = {
                  ...source,
                  html: `<div class="pswp__item--${mediaType}">${media.querySelector(htmlTag).outerHTML}</div>`,
                };
                break;
              default: // Image.
                source = {
                  ...source,
                  src: src,
                  width: pswpWidth,
                  height: pswpHeight,
                };
                break;
            }

            dataSource.push(source);
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        let lastLightboxIndex = null;
        this.lightbox.on('close', () => {
          if (this.lightbox?.pswp?.currIndex) {
            lastLightboxIndex = this.lightbox.pswp.currIndex;
          }
        });

        this.lightbox.on('destroy', () => {
          const slider = this.sliderInstance?.slider;

          if (slider && lastLightboxIndex !== null) {
            const targetIndex = lastLightboxIndex;
            if (targetIndex >= 0 && targetIndex < slider.slides.length && slider.activeIndex !== targetIndex) {
              requestAnimationFrame(() => {
                slider.slideTo(targetIndex, 0);
              });
            }
            lastLightboxIndex = null;
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              },
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              },
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox?.pswp?.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          console.log('slide changed to', activeIndex);
          console.log('thumbs from swiper event', thumbs);
          console.log('this.thumbsInstance', this.thumbsInstance);
          console.log('this.thumbsInstance.slider', this.thumbsInstance?.slider);

          if (this.thumbsInstance && this.thumbsInstance.slider) {
            this.thumbsInstance.slider.slideTo(Math.max(0, activeIndex - 1));
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image',
          };
        }

        if (!featuredMedia) return;

        if (this.sliderInstance.slider) {
          const slideIndex = featuredMedia.position || 0;
          this.sliderInstance.slider.slideTo(slideIndex - 1, 0, false);
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      setVariantMedia(variantId) {
        const dataEl = document.getElementById(`VariantMediaData-${this.id.replace('MediaGallery-', '')}`);
        if (!dataEl) return;

        let allData;
        try {
          allData = JSON.parse(dataEl.textContent);
        } catch(e) {
          console.error('VariantMediaData parse error', e);
          return;
        }

        const variantData = allData[String(variantId)];

        // No variant images — restore product media and bail
        if (!variantData || !variantData.urls || variantData.urls.length === 0) {
          this.restoreProductMedia();
          return;
        }

        const slidesHTML = variantData.urls.map((url, i) => {
          const id = variantData.ids[i];
          const width = variantData.widths[i];
          const height = variantData.heights[i];
          return `
            <div
              tabindex="-1"
              class="product__media-item swiper-slide"
              data-media-id="${id}"
              data-media-type="image"
              data-media-index="${i}"
              data-src="${url}"
              data-pswp-width="${width}"
              data-pswp-height="${height}"
            >
              <div class="product__media-item-container overflow-hidden blocks-radius media-type-image" style="width:100%;aspect-ratio:1.333;">
                <motion-element data-motion="zoom-out-sm" class="block product__media media-wrapper" style="width:100%;height:100%;">
                  <div class="no-empty" style="height:100%;background-image:url('${url}');background-size:cover;background-position:center;"></div>
                </motion-element>
              </div>
            </div>
          `;
        }).join('');

        const thumbsHTML = variantData.urls.map((url, i) => {
          const id = variantData.ids[i];
          return `
            <div class="product__thumbs-item swiper-slide" data-target="${id}" data-media-position="${i + 1}">
              <div class="product__thumbs-container relative blocks-radius-md media-wrapper cursor-pointer">
                <div class="product__thumbs-inner" style="--aspect-ratio: 1">
                  <img src="${variantData.thumbs[i]}" loading="lazy" is="image-lazy">
                </div>
              </div>
            </div>
          `;
        }).join('');

        const sliderEl = this.querySelector('[id^="Slider-Gallery"]');
        const thumbsEl = this.querySelector('[id^="Slider-Thumbnails"]');

        if (sliderEl) sliderEl.innerHTML = slidesHTML;
        if (thumbsEl) thumbsEl.innerHTML = thumbsHTML;

        this.destroySlider();
        if (typeof this.thumbsInstance === 'object') {
          this.thumbsInstance.slider.destroy();
          this.thumbsInstance = false;
        }
        this.initialized = false;
        this.init();
      }

      restoreProductMedia() {
        const sliderEl = this.querySelector('[id^="Slider-Gallery"]');
        const thumbsEl = this.querySelector('[id^="Slider-Thumbnails"]');

        if (sliderEl && this._productSlidesHTML) sliderEl.innerHTML = this._productSlidesHTML;
        if (thumbsEl && this._productThumbsHTML) thumbsEl.innerHTML = this._productThumbsHTML;

        this.destroySlider();
        if (typeof this.thumbsInstance === 'object') {
          this.thumbsInstance.slider.destroy();
          this.thumbsInstance = false;
        }
        this.initialized = false;
        this.init();
      }

      swapVariantMediaHTML(variantId) {
        const dataEl = document.getElementById(`VariantMediaData-${this.id.replace('MediaGallery-', '')}`);
        if (!dataEl) return;

        let allData;
        try {
          allData = JSON.parse(dataEl.textContent);
        } catch(e) {
          return;
        }

        const variantData = allData[String(variantId)];

        // No variant images for the initially selected variant — leave product media as-is
        if (!variantData || !variantData.urls || variantData.urls.length === 0) return;

        const slidesHTML = variantData.urls.map((url, i) => {
          const id = variantData.ids[i];
          const width = variantData.widths[i];
          const height = variantData.heights[i];
          return `
            <div
              tabindex="-1"
              class="product__media-item swiper-slide"
              data-media-id="${id}"
              data-media-type="image"
              data-media-index="${i}"
              data-src="${url}"
              data-pswp-width="${width}"
              data-pswp-height="${height}"
            >
              <div class="product__media-item-container overflow-hidden blocks-radius media-type-image" style="width:100%;aspect-ratio:1.333;">
                <motion-element data-motion="zoom-out-sm" class="block product__media media-wrapper" style="width:100%;height:100%;">
                  <div class="no-empty" style="height:100%;background-image:url('${url}');background-size:cover;background-position:center;"></div>
                </motion-element>
              </div>
            </div>
          `;
        }).join('');

        const thumbsHTML = variantData.urls.map((url, i) => {
          const id = variantData.ids[i];
          return `
            <div class="product__thumbs-item swiper-slide" data-target="${id}" data-media-position="${i + 1}">
              <div class="product__thumbs-container relative blocks-radius-md media-wrapper cursor-pointer">
                <div class="product__thumbs-inner" style="--aspect-ratio: 1">
                  <img src="${variantData.thumbs[i]}" loading="lazy" is="image-lazy">
                </div>
              </div>
            </div>
          `;
        }).join('');

        const sliderEl = this.querySelector('[id^="Slider-Gallery"]');
        const thumbsEl = this.querySelector('[id^="Slider-Thumbnails"]');

        if (sliderEl) sliderEl.innerHTML = slidesHTML;
        if (thumbsEl) thumbsEl.innerHTML = thumbsHTML;
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image',
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
